/**
 * Product Drawer Component - Exact HTML example
 * Handles product quick view in a slide-out drawer
 */

class ProductDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this;
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.container = this.querySelector('.quick-cart-drawer__blocks');
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.content = this.querySelector('[data-drawer-content]');
    this.template = document.getElementById('product-drawer-template');

    this.currentProduct = null;
    this.currentVariant = null;
    this.glideInstance = null;
    this.isOpen = false;
    this.isOpening = false;
    this.stateToken = 0;
    this.openRaf = null;
    this.closeTimer = null;
    this.closeTransitionHandler = null;
    this.previouslyFocusedElement = null;
    this.activeRequest = null;

    // Get theme settings from Shopify
    this.settings = window.theme?.settings || window.Shopify?.theme?.settings || {};

    this.bindEvents();
    this.applySettings();
  }

  bindEvents() {
    // Close drawer events
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is--open')) {
        this.close();
      }
    });

    // Prevent body scroll when drawer is open
    this.addEventListener('open', () => {
      document.body.style.overflow = 'hidden';
    });

    this.addEventListener('close', () => {
      if (!document.querySelector('cart-drawer-modern.is--open')) {
        document.body.style.overflow = '';
      }
    });
  }

  applySettings() {
    // Apply position setting
    const position = this.settings.product_drawer_position || 'right';
    this.setAttribute('data-position', position);

    // Apply width setting
    const width = this.settings.product_drawer_width || 600;
    this.style.setProperty('--drawer-width', `${width}px`);

    // Apply typography settings
    const titleSize = this.settings.product_drawer_title_size || 20;
    const titleWeight = this.settings.product_drawer_title_weight || 600;
    const priceSize = this.settings.product_drawer_price_size || 24;
    const priceWeight = this.settings.product_drawer_price_weight || 700;

    this.style.setProperty('--title-font-size', `${titleSize}px`);
    this.style.setProperty('--title-font-weight', titleWeight);
    this.style.setProperty('--price-font-size', `${priceSize}px`);
    this.style.setProperty('--price-font-weight', priceWeight);

    // Apply variant settings - get from data attributes or settings
    const variantSize = this.dataset.variantSize || this.settings.product_drawer_variant_size || 48;
    const variantBg = this.dataset.variantBg || this.settings.product_drawer_variant_bg_color || '#FFFFFF';
    const variantText = this.dataset.variantText || this.settings.product_drawer_variant_text_color || '#000000';
    const variantSelectedBg =
      this.dataset.variantSelectedBg || this.settings.product_drawer_variant_selected_bg_color || '#000000';
    const variantSelectedText =
      this.dataset.variantSelectedText || this.settings.product_drawer_variant_selected_text_color || '#FFFFFF';
    const variantFontSize = this.dataset.variantFontSize || this.settings.product_drawer_variant_font_size || 14;
    const variantFontWeight = this.dataset.variantFontWeight || this.settings.product_drawer_variant_font_weight || 500;

    this.style.setProperty('--variant-size', `${variantSize}px`);
    this.style.setProperty('--variant-bg', variantBg);
    this.style.setProperty('--variant-text', variantText);
    this.style.setProperty('--variant-selected-bg', variantSelectedBg);
    this.style.setProperty('--variant-selected-text', variantSelectedText);
    this.style.setProperty('--variant-font-size', `${variantFontSize}px`);
    this.style.setProperty('--variant-font-weight', variantFontWeight);

    // Apply button settings - get from data attributes or settings
    const buttonFontSize = this.dataset.buttonFontSize || this.settings.product_drawer_button_font_size || 14;
    const buttonFontWeight = this.dataset.buttonFontWeight || this.settings.product_drawer_button_font_weight || 600;

    this.style.setProperty('--button-font-size', `${buttonFontSize}px`);
    this.style.setProperty('--button-font-weight', buttonFontWeight);
  }

  waitForStylesheet() {
    const stylesheet = document.getElementById('ProductDrawerStyles');

    if (!stylesheet) {
      return Promise.resolve(window.getComputedStyle(this).position === 'fixed');
    }

    if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
      return Promise.resolve(true);
    }

    if (stylesheet.dataset.loadState === 'error') {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      const cleanup = () => {
        stylesheet.removeEventListener('load', onLoad);
        stylesheet.removeEventListener('error', onError);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const finish = (ready) => {
        if (settled) return;
        settled = true;
        cleanup();

        if (!ready) {
          resolve(false);
          return;
        }

        requestAnimationFrame(() => resolve(true));
      };

      const onLoad = () => finish(true);
      const onError = () => finish(false);

      stylesheet.addEventListener('load', onLoad, { once: true });
      stylesheet.addEventListener('error', onError, { once: true });
      timeoutId = setTimeout(() => finish(false), 5000);

      if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
        finish(true);
      } else if (stylesheet.dataset.loadState === 'error') {
        finish(false);
      }
    });
  }

  getTransitionDuration(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const parseTimeList = (value) =>
      value.split(',').map((time) => {
        const normalized = time.trim();
        const parsed = Number.parseFloat(normalized) || 0;
        return normalized.endsWith('ms') ? parsed : parsed * 1000;
      });

    const durations = parseTimeList(styles.transitionDuration);
    const delays = parseTimeList(styles.transitionDelay);
    const itemCount = Math.max(durations.length, delays.length);
    let maximum = 0;

    for (let index = 0; index < itemCount; index += 1) {
      const duration = durations[index % durations.length] || 0;
      const delay = delays[index % delays.length] || 0;
      maximum = Math.max(maximum, duration + delay);
    }

    return maximum;
  }

  clearCloseWait() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.container && this.closeTransitionHandler) {
      this.container.removeEventListener('transitionend', this.closeTransitionHandler);
      this.closeTransitionHandler = null;
    }
  }

  hideAfterClose(token) {
    this.clearCloseWait();

    const finish = () => {
      if (token !== this.stateToken || this.isOpen) return;
      this.clearCloseWait();
      this.hidden = true;

      if (this.glideInstance) {
        this.glideInstance.destroy();
        this.glideInstance = null;
      }

      this.content.innerHTML = '';
      this.currentProduct = null;
      this.currentVariant = null;
    };

    const duration = this.getTransitionDuration(this.container);
    if (!this.container || duration === 0) {
      finish();
      return;
    }

    this.closeTransitionHandler = (event) => {
      if (event.target === this.container && event.propertyName === 'transform') {
        finish();
      }
    };

    this.container.addEventListener('transitionend', this.closeTransitionHandler);
    this.closeTimer = setTimeout(finish, duration + 100);
  }

  redirectToProduct(productHandle, variantId) {
    const root = window.Shopify?.routes?.root || window.routes?.root_url || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const variantQuery = variantId ? `?variant=${encodeURIComponent(variantId)}` : '';
    window.location.assign(`${normalizedRoot}products/${encodeURIComponent(productHandle)}${variantQuery}`);
  }

  trapFocus() {
    this.releaseFocus();
    this.focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        const styles = window.getComputedStyle(element);
        return !element.hidden && !element.closest('[hidden], [inert]') && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
      });

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    this.addEventListener('keydown', this.focusTrapHandler);
  }

  releaseFocus() {
    if (!this.focusTrapHandler) return;
    this.removeEventListener('keydown', this.focusTrapHandler);
    this.focusTrapHandler = null;
  }

  async open(productHandle, variantId = null) {
    if (!productHandle) return false;

    const token = ++this.stateToken;
    this.isOpening = true;
    this.clearCloseWait();

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    if (this.activeRequest) {
      this.activeRequest.abort();
      this.activeRequest = null;
    }

    const stylesReady = await this.waitForStylesheet();
    if (token !== this.stateToken) return false;

    this.isOpening = false;
    if (!stylesReady) {
      this.redirectToProduct(productHandle, variantId);
      return false;
    }

    const visuallyOpen = this.classList.contains('is--open');
    this.isOpen = true;
    document.body.style.overflow = 'hidden';

    if (!visuallyOpen) {
      this.previouslyFocusedElement = document.activeElement;
      this.hidden = false;
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');

      void this.container?.offsetWidth;
      this.openRaf = requestAnimationFrame(() => {
        this.openRaf = null;
        if (token !== this.stateToken || !this.isOpen) return;
        this.classList.add('is--open');
        this.trapFocus();
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    this.showLoading();
    const request = new AbortController();
    this.activeRequest = request;

    try {
      const product = await this.fetchProduct(productHandle, request.signal);
      if (token !== this.stateToken || !this.isOpen) return false;

      this.currentProduct = product;

      if (variantId) {
        this.currentVariant = product.variants.find((variant) => variant.id == variantId);
      } else {
        this.currentVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      }

      this.render();
      this.hideLoading();
      this.closeButton?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('open', { detail: { product } }));
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;
      if (token !== this.stateToken || !this.isOpen) return false;

      console.error('Error loading product:', error);
      this.hideLoading();
      this.showError();
      return false;
    } finally {
      if (this.activeRequest === request) {
        this.activeRequest = null;
      }
    }
  }

  close({ restoreFocus = true, focusTarget = null } = {}) {
    if (!this.isOpen && !this.isOpening) return;

    const wasOpen = this.isOpen;
    const token = ++this.stateToken;
    this.isOpen = false;
    this.isOpening = false;

    if (this.activeRequest) {
      this.activeRequest.abort();
      this.activeRequest = null;
    }

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    if (!wasOpen) return;

    this.classList.remove('is--open');
    this.releaseFocus();

    const nextFocus = focusTarget || (restoreFocus ? this.previouslyFocusedElement : null);
    if (nextFocus instanceof HTMLElement && nextFocus.isConnected && !this.contains(nextFocus)) {
      nextFocus.focus({ preventScroll: true });
    }
    this.previouslyFocusedElement = null;

    this.inert = true;
    this.setAttribute('aria-hidden', 'true');

    if (!document.querySelector('cart-drawer-modern.is--open')) {
      document.body.style.overflow = '';
    }

    this.hideAfterClose(token);
    this.dispatchEvent(new CustomEvent('close'));
  }

  async fetchProduct(handle, signal) {
    const response = await fetch(`/products/${handle}.js`, { signal });
    if (!response.ok) throw new Error('Product not found');
    const product = await response.json();

    return product;
  }

  render() {
    if (!this.currentProduct || !this.template) return;

    // Clone template
    const content = this.template.content.cloneNode(true);

    // Update images swiper
    const slidesContainer = content.querySelector('[data-drawer-slides]');
    if (slidesContainer) {
      let slidesHtml = '';

      // Use product images or fallback to featured image
      const images =
        this.currentProduct.images && this.currentProduct.images.length > 0
          ? this.currentProduct.images
          : [this.currentProduct.featured_image || ''];

      images.forEach((image, index) => {
        if (image) {
          // Format Shopify image URL properly
          let imageUrl = image;
          if (!imageUrl.includes('&width=')) {
            // If width parameter is missing, add it
            imageUrl = imageUrl.includes('?') ? `${imageUrl}&width=720` : `${imageUrl}?width=720`;
          }

          slidesHtml += `
            <li class="glide__slide" data-media-id="image-${index}" data-media-type="image" data-index="${index}">
                <div class="media media--portrait">
                    <img
                        src="${imageUrl}"
                        alt="${this.currentProduct.title}"
                        loading="${index === 0 ? 'eager' : 'lazy'}"
                    />
                </div>
            </li>
          `;
        }
      });

      slidesContainer.innerHTML = slidesHtml;
    }

    // Update title and link
    const title = content.querySelector('[data-drawer-title]');
    const titleSpan = content.querySelector('[data-drawer-title] .text-link--on-load');
    if (title && titleSpan) {
      title.href = `/products/${this.currentProduct.handle}`;
      title.title = this.currentProduct.title;
      titleSpan.textContent = this.currentProduct.title;
    }

    // Add vendor if enabled
    if (this.settings.product_drawer_show_vendor && this.currentProduct.vendor) {
      const titleWrapper = content.querySelector('.product__title-wrapper');
      if (titleWrapper) {
        const vendorHtml = `<div class="product__vendor">${this.currentProduct.vendor}</div>`;
        titleWrapper.insertAdjacentHTML('afterbegin', vendorHtml);
      }
    }

    // Update price
    const priceContainer = content.querySelector('[data-drawer-price]');
    if (priceContainer) {
      this.renderPrice(priceContainer);
    }

    // Update SKU
    const skuContainer = content.querySelector('[data-drawer-sku]');
    if (skuContainer) {
      this.renderSKU(skuContainer);
    }

    // Add description if enabled
    if (this.settings.product_drawer_show_description && this.currentProduct.description) {
      const priceElement = content.querySelector('.product__price');
      if (priceElement) {
        const descriptionHtml = `<div class="product__description">${this.currentProduct.description}</div>`;
        priceElement.insertAdjacentHTML('afterend', descriptionHtml);
      }
    }

    // Add rating if enabled - placeholder for now as rating data needs to come from metafields
    if (this.settings.product_drawer_show_rating) {
      const titleElement = content.querySelector('.product__title-wrapper');
      if (titleElement) {
        // This would need actual rating data from product metafields
        const ratingHtml = `<div class="product__rating" data-rating="0">
          <span class="visually-hidden">No reviews</span>
        </div>`;
        titleElement.insertAdjacentHTML('afterend', ratingHtml);
      }
    }

    // Render variants
    const variantsContainer = content.querySelector('[data-drawer-variants]');
    if (variantsContainer) {
      this.renderVariants(variantsContainer);
    }

    // Update form
    const form = content.querySelector('form');
    const variantInput = content.querySelector('[data-variant-input]');
    const productInput = content.querySelector('[data-product-input]');
    if (form && variantInput && productInput) {
      variantInput.value = this.currentVariant.id;
      productInput.value = this.currentProduct.id;
    }

    // Insert content
    this.content.innerHTML = '';
    this.content.appendChild(content);

    // Initialize form
    this.initializeForm();

    // Initialize Glide after DOM is ready
    setTimeout(() => {
      this.initializeGlide();
    }, 100);
  }

  initializeGlide() {
    const glideElement = this.content.querySelector('[data-drawer-glide]');
    if (!glideElement || !window.Glide) return;

    // Destroy existing instance if any
    if (this.glideInstance) {
      this.glideInstance.destroy();
    }

    // Get settings from Shopify theme settings
    const enableLoop = this.settings.product_drawer_enable_loop || false;
    const showArrows = this.settings.product_drawer_show_arrows || false;
    const showDots = this.settings.product_drawer_show_dots || false;

    // Initialize new Glide instance
    this.glideInstance = new Glide(glideElement, {
      type: enableLoop ? 'carousel' : 'slider',
      perView: 2.05, // 2 full + 0.05 partial view
      gap: 10,
      peek: 0,
      bound: !enableLoop,
      rewind: enableLoop,
      focusAt: 0,
      breakpoints: {
        750: {
          perView: 2, // Mobilde 2 resim yan yana
          gap: 8, // Resimler arası boşluk
        },
      },
    });

    this.glideInstance.mount();

    // Generate dots if needed
    const dotsContainer = glideElement.querySelector('[data-drawer-dots]');
    if (dotsContainer && showDots) {
      const slideCount = this.currentProduct.images?.length || 1;
      let dotsHtml = '';
      for (let i = 0; i < slideCount; i++) {
        dotsHtml += `<button class="glide__bullet glide__bullet--${
          this.settings.product_drawer_dots_style || 'round'
        }" data-glide-dir="=${i}"></button>`;
      }
      dotsContainer.innerHTML = dotsHtml;
    }
  }

  renderPrice(container) {
    const variant = this.currentVariant;

    const formatMoney = (cents) => {
      try {
        // Check currency display format setting
        const currencyFormat = window.theme?.settings?.currency_display_format || 'symbol_only';

        if (currencyFormat === 'code_only') {
          // Show only currency code without symbol
          const value = (cents / 100).toFixed(2);
          const currency = window.theme?.currency || 'USD';
          return `${value} ${currency}`;
        } else if (currencyFormat === 'symbol_and_code') {
          // Show both symbol and code
          const moneyWithCurrencyFormat =
            window.theme?.moneyWithCurrencyFormat || window.theme?.moneyFormat || '${{amount}} USD';
          if (Shopify?.formatMoney && moneyWithCurrencyFormat) {
            try {
              return Shopify.formatMoney(cents, moneyWithCurrencyFormat);
            } catch (e) {
              console.error('Shopify.formatMoney error:', e);
              return `${(cents / 100).toFixed(2)}`;
            }
          }
          return `${(cents / 100).toFixed(2)}`;
        } else {
          // Default: symbol only
          const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
          if (Shopify?.formatMoney && moneyFormat) {
            try {
              return Shopify.formatMoney(cents, moneyFormat);
            } catch (e) {
              console.error('Shopify.formatMoney error:', e);
              return `$${(cents / 100).toFixed(2)}`;
            }
          }
          return `$${(cents / 100).toFixed(2)}`;
        }
      } catch (error) {
        console.error('Money formatting error:', error);
        // Fallback to simple formatting
        return `$${(cents / 100).toFixed(2)}`;
      }
    };

    let priceHtml = '';

    if (variant.compare_at_price > variant.price) {
      priceHtml = `
        <div class="price">
          <div class="price__container">
            <div class="price__sale">
              <div class="price__sale-inner">
                <span class="visually-hidden">Sale price</span>
                ${formatMoney(variant.price)}
              </div>
            </div>
            <div class="price__regular">
              <span class="visually-hidden">Regular price</span>
              <s>${formatMoney(variant.compare_at_price)}</s>
            </div>
          </div>
        </div>
      `;
    } else {
      priceHtml = `
        <div class="price">
          <div class="price__container">
            <div class="price__sale">
              <div class="price__sale-inner">
                <span class="visually-hidden">Regular price</span>
                ${formatMoney(variant.price)}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    container.innerHTML = priceHtml;
  }

  renderSKU(container) {
    const variant = this.currentVariant;

    if (!variant || !variant.sku) {
      container.innerHTML = '';
      return;
    }

    const skuHtml = `
      <div class="product__sku-wrapper">
        <span class="product__sku-label">SKU:</span>
        <span class="product__sku-value">${variant.sku}</span>
      </div>
    `;

    container.innerHTML = skuHtml;
  }

  renderVariants(container) {
    // Check if product has options to display
    if (!this.currentProduct.options || this.currentProduct.options.length === 0) {
      return;
    }

    // Auto-select hidden options (like Color if it's single value)
    this.autoSelectHiddenOptions();

    let html = '';

    // Try to get actual option names from product
    if (this.currentProduct.options && this.currentProduct.options.length > 0) {
      this.currentProduct.options.forEach((option, index) => {
        const optionName = typeof option === 'string' ? option : option.name || `Option ${index + 1}`;

        // Skip "Title" option which is default in Shopify
        if (optionName.toLowerCase() === 'title') {
          return;
        }

        // Check if this is a size/beden option
        const optionNameLower = optionName.toLowerCase();
        const isSize = optionNameLower.includes('size') || optionNameLower.includes('beden');

        // Only show Size/Beden options, skip Color/Renk
        if (!isSize) {
          return;
        }

        // Get unique values for this option
        const values = [...new Set(this.currentProduct.variants.map((v) => v.options[index]))];

        // Skip if only one value (no need to show single option)
        if (values.length <= 1) {
          return;
        }

        // Show if there are multiple values
        if (values.length > 0 && values[0] !== 'Default Title') {
          html += `
            <li>
                <fieldset class="product__variant-options js-product-card-options">
                    <legend>${optionName}</legend>
          `;

          values.forEach((value, valueIndex) => {
            const variant = this.getVariantByOption(index, value);
            // Check if this option value matches current variant's option
            const isChecked = this.currentVariant && this.currentVariant.options[index] === value;
            // Bir seçenek için tüm kombinasyonları kontrol et
            const isAvailable = this.isOptionValueAvailable(index, value);
            const uniqueId = `drawer-variant-option-${index}-${valueIndex}`;

            html += `
                    <div class="button--variant" tabindex="0">
                        <input
                            id="${uniqueId}"
                            type="radio"
                            name="drawer-option[${index}]"
                            class="product-option__input hidden variant-option-radio-input visually-hidden"
                            value="${value}"
                            aria-label="${value}"
                            data-variant-quantity="${variant ? variant.inventory_quantity || 0 : 0}"
                            data-option-index="${index}"
                            ${isChecked ? 'checked' : ''}
                            ${!isAvailable ? 'disabled' : ''}
                        />
                        <label for="${uniqueId}" title="${value}" class="product-option__label product-option__label--${
              this.settings.product_drawer_variant_style || 'bordered'
            }">
                            <span>${value}</span>
                        </label>
                    </div>
            `;
          });

          html += `
                </fieldset>
            </li>
          `;
        }
      });
    }

    container.innerHTML = html;

    // Add change listeners
    container.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => this.onVariantChange());
    });
  }

  autoSelectHiddenOptions() {
    // Auto-select options that won't be displayed (like single Color option)
    // This function should NOT change current variant if it's already available
    if (!this.currentProduct.options) return;

    // If current variant is already available, don't change it
    if (this.currentVariant && this.currentVariant.available) {
      return;
    }

    // Only run if current variant is not available
    // Try to find a matching available variant with hidden options auto-selected
    const selectedOptions = [];

    this.currentProduct.options.forEach((option, index) => {
      const optionName = typeof option === 'string' ? option : option.name || '';
      const optionNameLower = optionName.toLowerCase();
      const isSize = optionNameLower.includes('size') || optionNameLower.includes('beden');
      const values = [...new Set(this.currentProduct.variants.map((v) => v.options[index]))];

      // If not a size option OR only one value, auto-select it
      if (!isSize || values.length === 1) {
        selectedOptions[index] = this.currentVariant?.options[index] || values[0];
      } else {
        // For size options, keep current variant's value
        selectedOptions[index] = this.currentVariant?.options[index];
      }
    });

    // Try to find an available variant with these options
    const variant = this.currentProduct.variants.find((v) => {
      return (
        v.available &&
        v.options.every((option, idx) => {
          return !selectedOptions[idx] || option === selectedOptions[idx];
        })
      );
    });

    if (variant) {
      this.currentVariant = variant;
    }
  }

  getVariantByOption(optionIndex, value) {
    return this.currentProduct.variants.find((variant) => {
      return variant.options[optionIndex] === value;
    });
  }

  // Bir seçenek değerinin herhangi bir kombinasyonda stokta olup olmadığını kontrol et
  isOptionValueAvailable(optionIndex, value) {
    return this.currentProduct.variants.some((variant) => {
      return variant.options[optionIndex] === value && variant.available;
    });
  }

  onVariantChange() {
    const selectedOptions = [];

    // First, fill in hidden/auto-selected options
    this.currentProduct.options.forEach((option, index) => {
      const optionName = typeof option === 'string' ? option : option.name || '';
      const optionNameLower = optionName.toLowerCase();
      const isSize = optionNameLower.includes('size') || optionNameLower.includes('beden');
      const values = [...new Set(this.currentProduct.variants.map((v) => v.options[index]))];

      // Auto-select hidden options
      if (!isSize || values.length === 1) {
        selectedOptions[index] = this.currentVariant?.options[index] || values[0];
      }
    });

    // Then, override with user-selected visible options
    this.content.querySelectorAll('[data-option-index]').forEach((input) => {
      if (input.checked) {
        selectedOptions[parseInt(input.dataset.optionIndex)] = input.value;
      }
    });

    // Find matching variant
    const variant = this.currentProduct.variants.find((v) => {
      return v.options.every((option, index) => option === selectedOptions[index]);
    });

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantInfo();
    }
  }

  updateVariantInfo() {
    // Update price
    const priceContainer = this.content.querySelector('[data-drawer-price]');
    if (priceContainer) {
      this.renderPrice(priceContainer);
    }

    // Update SKU
    const skuContainer = this.content.querySelector('[data-drawer-sku]');
    if (skuContainer) {
      this.renderSKU(skuContainer);
    }

    // Update form variant ID
    const variantInput = this.content.querySelector('[data-variant-input]');
    if (variantInput) {
      variantInput.value = this.currentVariant.id;
    }

    // Update button state
    const addButton = this.content.querySelector('[data-drawer-add-button]');
    if (addButton) {
      const buttonSpan = addButton.querySelector('span');
      addButton.disabled = !this.currentVariant.available;
      if (buttonSpan) {
        buttonSpan.textContent = this.currentVariant.available ? 'Add to cart' : 'Sold out';
      }
    }

    // Selected variant display removed for cleaner UI
  }

  initializeForm() {
    const form = this.content.querySelector('form');
    if (!form) return;

    // Quantity buttons
    const quantityInput = form.querySelector('input[name="quantity"]');
    const decrementButton = form.querySelector('button[name="minus"]');
    const incrementButton = form.querySelector('button[name="plus"]');

    decrementButton?.addEventListener('click', () => {
      const current = parseInt(quantityInput.value) || 1;
      if (current > 1) {
        quantityInput.value = current - 1;
      }
    });

    incrementButton?.addEventListener('click', () => {
      const current = parseInt(quantityInput.value) || 1;
      quantityInput.value = current + 1;
    });

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.addToCart(form);
    });

    // Buy it now button
    const buyNowButton = this.content.querySelector('[data-drawer-buy-now]');
    buyNowButton?.addEventListener('click', async () => {
      await this.addToCart(form, true);
    });
  }

  async addToCart(form, buyNow = false) {
    const submitButton = form.querySelector('[type="submit"]');
    const spinner = submitButton?.querySelector('.loading-overlay__spinner');
    const operationToken = this.stateToken;

    // Show loading
    if (submitButton) submitButton.disabled = true;
    if (spinner) spinner.hidden = false;

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const item = await response.json();
      const ownsDrawerState = operationToken === this.stateToken && this.isOpen && form.isConnected;

      if (!ownsDrawerState) {
        document.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: { item, buyNow, openDrawer: false },
          })
        );
        return;
      }

      if (buyNow) {
        document.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: { item, buyNow, openDrawer: false },
          })
        );
        // Redirect to checkout
        window.location.href = '/checkout';
      } else {
        // Finish the product modal transition before handing focus to the cart modal.
        const cartTrigger = document.querySelector('[data-cart-drawer-trigger]');
        this.close({ restoreFocus: false, focusTarget: cartTrigger });
        const closeToken = this.stateToken;
        const closeDuration = this.getTransitionDuration(this.container);
        await new Promise((resolve) => setTimeout(resolve, closeDuration + 100));
        const canOpenCart = closeToken === this.stateToken && !this.isOpen;

        document.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: { item, buyNow, openDrawer: canOpenCart },
          })
        );

        // Update cart bubble
        fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            window.SEMUHeader?.renderCartCount?.(cart.item_count, { animate: true });
          });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Show error message
    } finally {
      if (submitButton) submitButton.disabled = false;
      if (spinner) spinner.hidden = true;
    }
  }

  showLoading() {
    this.classList.add('product-drawer--loading');
  }

  hideLoading() {
    this.classList.remove('product-drawer--loading');
  }

  showError() {
    this.content.innerHTML = `
      <div class="quick-cart-drawer__main--empty">
        <p>Sorry, there was an error loading the product.</p>
      </div>
    `;
  }
}

// Register custom element
customElements.define('quick-cart-drawer', ProductDrawer);

// Initialize drawer on quick add buttons
document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('quick-cart-drawer');

  // Listen for quick add events
  document.addEventListener('click', (e) => {
    const quickAddButton = e.target.closest('[data-quick-add-drawer]');
    if (quickAddButton && drawer) {
      e.preventDefault();

      const productHandle = quickAddButton.dataset.productHandle;
      const variantId = quickAddButton.dataset.variantId;

      drawer.open(productHandle, variantId);
    }
  });

  // Glide.js already loaded in theme
});
